import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from './ThemeContext';
import './App.css';

function CouponUpload() {
  const navigate = useNavigate();
  const { theme, toggleTheme, isDark } = useTheme();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Excel upload states
  const [excelFile, setExcelFile] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState('');
  const [excelData, setExcelData] = useState(null);
  const [uploadMode, setUploadMode] = useState('session'); // 'session' or 'excel'

  // Dropdown visibility states
  const [showDiscountTypeDropdown, setShowDiscountTypeDropdown] = useState(null);

  useEffect(() => {
    try {
      const couponData = sessionStorage.getItem('couponData');
      if (couponData) {
        const parsedData = JSON.parse(couponData);
        setCoupons(parsedData);
        setTotalPages(Math.ceil(parsedData.length / 50));
      } else {
        setError('No coupon data found. Please go back and select coupons to upload.');
      }
    } catch (err) {
      console.error('Error accessing sessionStorage:', err);
      setError('Error loading coupon data. Please try again.');
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside discount type dropdown
      const isOutsideDiscountType = !event.target.closest('.discount-type-button') && !event.target.closest('.discount-type-dropdown');

      if (isOutsideDiscountType) {
        setShowDiscountTypeDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  const handleCellChange = (index, field, value) => {
    const globalIndex = (currentPage - 1) * 50 + index;
    const updatedCoupons = [...coupons];
    updatedCoupons[globalIndex][field] = value;
    setCoupons(updatedCoupons);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!allowedTypes.includes(file.type)) {
        setExcelError('Please select a valid Excel file (.xlsx or .xls)');
        setExcelFile(null);
        return;
      }
      setExcelFile(file);
      setExcelError('');
      setExcelData(null); // Clear previous data
    }
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      setExcelError('Please select an Excel file first');
      return;
    }

    // Clear previous messages
    setSuccessMessage('');
    setError('');

    setExcelLoading(true);
    setExcelError('');

    try {
      console.log('📊 EXCEL UPLOAD: Starting Excel file upload');

      const formData = new FormData();
      formData.append('excel_file', excelFile);

      const response = await axios.post('/api/coupons/upload-excel/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 second timeout for file upload
      });

      console.log('📊 EXCEL UPLOAD: API Response:', response.data);

      if (response.data.success) {
        console.log('✅ EXCEL UPLOAD: Successfully parsed', response.data.valid_coupons, 'coupons');

        // Set the parsed data
        setExcelData(response.data.data);
        setCoupons(response.data.data);
        setUploadMode('excel');
        setTotalPages(Math.ceil(response.data.data.length / 50));
        setCurrentPage(1);

        // Show success message
        showMessage(`✅ Successfully parsed ${response.data.valid_coupons} coupons from Excel file`, 'success');

        // Show validation errors if any
        if (response.data.validation_errors && response.data.validation_errors.length > 0) {
          const errorMsg = `⚠️ ${response.data.validation_errors.length} validation errors found. Check console for details.`;
          console.warn('📊 EXCEL UPLOAD: Validation errors:', response.data.validation_errors);
          showMessage(errorMsg, 'error');
        }

      } else {
        console.error('❌ EXCEL UPLOAD: Upload failed:', response.data.error);
        setExcelError(response.data.error);
      }

    } catch (err) {
      console.error('❌ EXCEL UPLOAD: Error during Excel upload:', err);

      let errorMessage = 'Failed to upload Excel file';

      if (err.response) {
        console.error('❌ EXCEL UPLOAD: Response status:', err.response.status);
        console.error('❌ EXCEL UPLOAD: Response data:', err.response.data);

        if (err.response.data && err.response.data.error) {
          errorMessage = `Server error: ${err.response.data.error}`;
        } else {
          errorMessage = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        console.error('❌ EXCEL UPLOAD: No response received:', err.request);
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        console.error('❌ EXCEL UPLOAD: Request setup error:', err.message);
        errorMessage = 'Error setting up request: ' + err.message;
      }

      setExcelError(errorMessage);
    }

    setExcelLoading(false);
  };

  const switchToSessionData = () => {
    try {
      const couponData = sessionStorage.getItem('couponData');
      if (couponData) {
        const parsedData = JSON.parse(couponData);
        setCoupons(parsedData);
        setUploadMode('session');
        setTotalPages(Math.ceil(parsedData.length / 50));
        setCurrentPage(1);
        setExcelData(null);
        setExcelFile(null);
        setExcelError('');
        showMessage('Switched to session coupon data', 'success');
      } else {
        setError('No session coupon data found');
      }
    } catch (err) {
      console.error('Error loading session data:', err);
      setError('Error loading session coupon data');
    }
  };

  const removeCoupon = (index) => {
    const globalIndex = (currentPage - 1) * 50 + index;
    const updatedCoupons = [...coupons];
    updatedCoupons.splice(globalIndex, 1);
    setCoupons(updatedCoupons);
    setTotalPages(Math.ceil(updatedCoupons.length / 50));
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  };

  const showMessage = (message, type = 'success') => {
    setError('');
    setErrorDetails([]);
    if (type === 'success') {
      setSuccessMessage(message);
    } else {
      setError(message);
    }
  };

  const handleUpload = async () => {
    // Clear previous messages
    setSuccessMessage('');
    setError('');
    setErrorDetails([]);

    setLoading(true);

    try {
      console.log('🎯 COUPON DATABASE: Starting coupon upload for', coupons.length, 'coupons');

      // Prepare data for database update
      const uploadData = {
        coupons: coupons.map(coupon => ({
          store_id: coupon.store_id,
          DiscountType: coupon.DiscountType,
          DiscountPercent: coupon.DiscountPercent,
          DealDefaultPromoCode: coupon.DealDefaultPromoCode
        }))
      };

      console.log('📊 COUPON DATABASE: Sending', uploadData.coupons.length, 'coupons to database API');

      // Send to database API
      const response = await axios.post('/api/coupons/upload/', uploadData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 60000, // 60 second timeout for large uploads
      });

      console.log('📊 COUPON DATABASE: API Response:', response.data);

      if (response.data.success) {
        console.log('✅ COUPON DATABASE: Successfully processed', response.data.coupons_processed, 'coupons and updated', response.data.products_updated, 'products');

        // Create detailed success message with store_id information
        let successMsg = `✅ Successfully processed ${response.data.coupons_processed} coupons and updated ${response.data.products_updated} products`;

        if (response.data.successful_store_ids && response.data.successful_store_ids.length > 0) {
          const storeList = response.data.successful_store_ids.slice(0, 5).join(', '); // Show first 5
          const remaining = response.data.successful_store_ids.length - 5;
          successMsg += ` for store_ids: ${storeList}${remaining > 0 ? ` (+${remaining} more)` : ''}`;
        }

        successMsg += ` at ${response.data.timestamp}`;
        showMessage(successMsg, 'success');

      } else {
        console.error('❌ COUPON DATABASE: Upload failed:', response.data.error);

        // Create detailed error message with store_id information
        let errorMsg = `❌ Upload completed with errors: ${response.data.error}`;

        if (response.data.successful_store_ids && response.data.successful_store_ids.length > 0) {
          const storeList = response.data.successful_store_ids.slice(0, 3).join(', '); // Show first 3
          const remaining = response.data.successful_store_ids.length - 3;
          errorMsg += `\n✅ Successful store_ids: ${storeList}${remaining > 0 ? ` (+${remaining} more)` : ''}`;
        }

        if (response.data.failed_store_ids && response.data.failed_store_ids.length > 0) {
          const storeList = response.data.failed_store_ids.slice(0, 3).join(', '); // Show first 3
          const remaining = response.data.failed_store_ids.length - 3;
          errorMsg += `\n❌ Failed store_ids: ${storeList}${remaining > 0 ? ` (+${remaining} more)` : ''}`;

          // Add specific error details if available
          if (response.data.store_errors) {
            const errorDetails = Object.entries(response.data.store_errors).slice(0, 2) // Show first 2 detailed errors
              .map(([storeId, error]) => `${storeId}: ${error}`)
              .join('; ');
            if (errorDetails) {
              errorMsg += `\nDetails: ${errorDetails}`;
            }
          }
        }

        showMessage(errorMsg, 'error');

        if (response.data.details) {
          console.error('❌ COUPON DATABASE: Error details:', response.data.details);
        }
      }

    } catch (err) {
      console.error('❌ COUPON DATABASE: Error during coupon upload:', err);

      let errorMessage = 'Failed to upload coupons to database';

      if (err.response) {
        console.error('❌ COUPON DATABASE: Response status:', err.response.status);
        console.error('❌ COUPON DATABASE: Response data:', err.response.data);

        if (err.response.data && err.response.data.error) {
          errorMessage = `Server error: ${err.response.data.error}`;
        } else {
          errorMessage = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        console.error('❌ COUPON DATABASE: No response received:', err.request);
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        console.error('❌ COUPON DATABASE: Request setup error:', err.message);
        errorMessage = 'Error setting up request: ' + err.message;
      }

      // Show error message
      showMessage(errorMessage, 'error');
    }

    setLoading(false);
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * 50;
    const endIndex = startIndex + 50;
    return coupons.slice(startIndex, endIndex);
  };

  // Discount type change handler
  const handleDiscountTypeChange = (index, newType) => {
    const globalIndex = (currentPage - 1) * 50 + index;

    if (globalIndex >= 0 && globalIndex < coupons.length) {
      const updatedCoupons = [...coupons];
      updatedCoupons[globalIndex] = {
        ...updatedCoupons[globalIndex],
        DiscountType: newType
      };

      setCoupons(updatedCoupons);
    }

    setShowDiscountTypeDropdown(null);
  };

  // Available discount type options
  const discountTypeOptions = ['PERCENT', 'FIXED', 'PERCENT_MAXIMUM', 'PERCENT_RANGE'];

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          className={`mx-1 px-3 py-1 rounded ${
            number === currentPage
              ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
              : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center mt-6">
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          First
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {items}
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    );
  };

  if (error && !coupons.length) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <button
            className="perzsi-button-secondary mt-4"
            onClick={() => navigate('/ads')}
          >
            ← Back to Ads Table
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <header className="perzsi-gradient shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <img
                  src="/perzsi-icon-black.png"
                  alt="Perzsi.com"
                  className="h-12 w-auto"
                />
                <p className="text-yellow-100">Coupon Upload Table</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-white text-right">
                <p className="text-sm opacity-90">Transformed Coupon Data</p>
              </div>
              <button
                onClick={toggleTheme}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200"
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Coupon Upload {uploadMode === 'excel' ? '(Excel Data)' : '(Session Data)'}
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Review and modify the {uploadMode === 'excel' ? 'parsed Excel' : 'transformed'} coupon data before uploading to database.
            </p>
            <button
              className="perzsi-button-primary w-full"
              onClick={handleUpload}
              disabled={loading || coupons.length === 0}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                `Upload ${coupons.length} Coupons to Database`
              )}
            </button>

            {successMessage && (
              <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg animate-fade-in">
                <div className="flex items-center">
                  <span className="text-lg mr-2">✅</span>
                  <span className="text-sm font-medium">{successMessage}</span>
                </div>
              </div>
            )}
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Upload Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Data Source:</span>
                <span className="font-semibold capitalize">{uploadMode}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Coupons:</span>
                <span className="font-semibold">{coupons.length}</span>
              </div>
              {uploadMode === 'excel' && excelData && (
                <div className="flex justify-between">
                  <span>Parsed from Excel:</span>
                  <span className="font-semibold text-green-600">✓</span>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t">
              <button
                className="perzsi-button-secondary w-full"
                onClick={() => navigate('/ads')}
              >
                ← Back to Ads Table
              </button>
            </div>
          </div>

          <div className="perzsi-card md:col-span-2">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Excel File Upload</h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Upload coupon data from Excel file. Expected columns: <strong>store_id</strong>, <strong>DiscountType</strong>, <strong>DiscountPercent</strong>, <strong>DealDefaultPromoCode</strong> (required), Name, Description, AdvertiserName (optional).
              Supports .xlsx and .xls formats.
            </p>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Excel File:
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-perzsi-yellow file:text-perzsi-gray hover:file:bg-yellow-400"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  className="perzsi-button-primary flex-1"
                  onClick={handleExcelUpload}
                  disabled={excelLoading || !excelFile}
                >
                  {excelLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Parsing...
                    </span>
                  ) : (
                    'Parse Excel File'
                  )}
                </button>

                {uploadMode === 'excel' && (
                  <button
                    className="perzsi-button-secondary"
                    onClick={switchToSessionData}
                  >
                    Switch to Session Data
                  </button>
                )}
              </div>

              {excelError && (
                <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">❌</span>
                    <span className="text-sm font-medium">{excelError}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <div className="font-semibold mb-2">
              ❌ {error}
            </div>
            {errorDetails && errorDetails.length > 0 && (
              <div className="mt-3 pt-3 border-t border-red-300">
                <div className="text-sm font-medium mb-2 text-red-800">
                  Error Details ({errorDetails.length} issues):
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {errorDetails.map((detail, index) => (
                    <div
                      key={index}
                      className="text-sm bg-red-50 px-2 py-1 rounded border-l-2 border-red-400"
                    >
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {coupons.length > 0 && (
          <div className="perzsi-table-container">
            <div className="overflow-x-auto">
              <table className="perzsi-table">
                <thead>
                  <tr>
                    <th>Advertiser</th>
                    <th>Store ID</th>
                    <th>Discount Type</th>
                    <th>Discount Percent</th>
                    <th>Discount Amount</th>
                    <th>Promo Code</th>
                    <th>Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentPageData().map((coupon, index) => (
                    <tr key={index} className={`hover:${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <td>{coupon.AdvertiserName}</td>
                      <td>
                        <input
                          type="text"
                          value={coupon.store_id || ''}
                          onChange={(e) => handleCellChange(index, 'store_id', e.target.value)}
                          className="perzsi-input"
                        />
                      </td>
                      <td>
                        <div className="relative">
                          <button
                            className={`discount-type-button px-2 py-1 rounded text-xs font-semibold cursor-pointer hover:opacity-80 ${
                              coupon.DiscountType === 'PERCENT'
                                ? 'bg-green-500 text-white'
                                : coupon.DiscountType === 'FIXED'
                                ? 'bg-blue-500 text-white'
                                : coupon.DiscountType === 'PERCENT_MAXIMUM'
                                ? 'bg-purple-500 text-white'
                                : coupon.DiscountType === 'PERCENT_RANGE'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-500 text-white'
                            }`}
                            onClick={() => setShowDiscountTypeDropdown(showDiscountTypeDropdown === index ? null : index)}
                            title="Click to change discount type"
                          >
                            {coupon.DiscountType} ▼
                          </button>
                          {showDiscountTypeDropdown === index && (
                            <div
                              className="discount-type-dropdown fixed bg-white border border-gray-300 rounded-md shadow-xl"
                              style={{
                                zIndex: 9999,
                                minWidth: '140px',
                                position: 'fixed'
                              }}
                              ref={(el) => {
                                if (el && showDiscountTypeDropdown === index) {
                                  const rect = el.previousSibling?.getBoundingClientRect();
                                  if (rect) {
                                    const dropdownHeight = discountTypeOptions.length * 32 + 16; // Estimate height
                                    const windowHeight = window.innerHeight;
                                    const bottomSpace = windowHeight - rect.bottom;

                                    if (bottomSpace < dropdownHeight && rect.top > dropdownHeight) {
                                      // Show above if not enough space below
                                      el.style.top = `${rect.top - dropdownHeight - 8}px`;
                                      el.style.left = `${rect.left}px`;
                                    } else {
                                      // Show below
                                      el.style.top = `${rect.bottom + 4}px`;
                                      el.style.left = `${rect.left}px`;
                                    }
                                  }
                                }
                              }}
                            >
                              {discountTypeOptions.map((type) => (
                                <div
                                  key={type}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDiscountTypeChange(index, type);
                                  }}
                                  onClick={(e) => {
                                    // Prevent default click behavior
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  className={`px-3 py-2 text-xs cursor-pointer hover:bg-gray-100 select-none ${
                                    coupon.DiscountType === type ? 'bg-yellow-50 text-perzsi-gray font-semibold' : 'text-gray-700'
                                  }`}
                                  style={{ userSelect: 'none' }}
                                >
                                  {type}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={coupon.DiscountPercent || ''}
                          onChange={(e) => handleCellChange(index, 'DiscountPercent', e.target.value)}
                          className="perzsi-input"
                        />
                      </td>
                      <td>{coupon.DiscountAmount}</td>
                      <td>
                        <input
                          type="text"
                          value={coupon.DealDefaultPromoCode || ''}
                          onChange={(e) => handleCellChange(index, 'DealDefaultPromoCode', e.target.value)}
                          className="perzsi-input"
                        />
                      </td>
                      <td>{coupon.Name}</td>
                      <td>
                        <button
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                          onClick={() => removeCoupon(index)}
                          title="Remove this coupon"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {renderPagination()}

        <div className="mt-6 text-center">
          <button
            className="perzsi-button-secondary"
            onClick={() => navigate('/ads')}
          >
            ← Back to Ads Table
          </button>
        </div>
      </div>
    </div>
  );
}

export default CouponUpload;
